import React from 'react';
import { observer } from 'mobx-react-lite';
import { PlusIcon } from '@radix-ui/react-icons';
import { IconButton } from '@producer/ui-kit';
import { useDroppable } from '@dnd-kit/react';

import { Tooltip } from '../../../../components/tooltip/Tooltip';

import styles from './task-column.module.scss';
type TaskColumnProps = {
  id: string;
  name: string;
  count: number;
  icon?: React.ReactNode;
  onCreate?: () => void;
  children?: React.ReactNode;
};

/**
 * A board column. This is a droppable container only — columns cannot be
 * reordered (their order is fully determined by the active grouping). Cards
 * are dropped here via the dnd-kit droppable API; empty columns remain valid
 * drop targets. The header `+` opens the create-task modal pre-filled for this
 * column (mirrors the list's `TaskCollection`).
 */
export const TaskColumn: React.FC<TaskColumnProps> = observer(
  ({ id, name, count, icon, onCreate, children }) => {
    const { ref, isDropTarget } = useDroppable({ id, type: 'column', accept: 'item' });

    return (
      <div className={styles['container']} data-drop-target={isDropTarget}>
        <div className={styles['header']}>
          <div className={styles['header-left']}>
            {icon}
            <span className={styles['name']}>{name}</span>
            <span className={styles['count']}>{count}</span>
          </div>

          {onCreate && (
            <Tooltip text="Create task">
              <IconButton icon={<PlusIcon />} onClick={onCreate} />
            </Tooltip>
          )}
        </div>

        <div className={styles['list']} ref={ref}>
          {children}
        </div>
      </div>
    );
  },
);
